<template>
  <WLLink
    ref="elRef"
    :type="buttonType"
    class="wl-button text-default inline-flex min-w-6 cursor-pointer items-center justify-center gap-1 overflow-visible rounded-2xl leading-5 font-medium whitespace-nowrap no-underline transition-colors duration-150"
    :class="[
      `type-${type}`,
      `variant-${variant}`,
      `size-${size}`,
      {
        'is-vertical': isVertical,
        'is-disabled !cursor-not-allowed opacity-20': isDisabled,
        'icon-only !px-0': !slots?.default && icon,
        'is-full-width w-full': isFullWidth,
        'gap-1': slots?.default && icon,
        'is-loading': isLoading,
      },
    ]"
    :disabled="linkDisabled"
    :fallback-component="as"
    :to="linkTo"
    :href="linkHref"
    @click="onClick"
  >
    <WLIcon
      v-if="prefixIcon"
      :name="prefixIcon"
      :size="IconSize.EXTRA_SMALL"
      :has-hover="false"
      class="shrink-0"
    />
    <template v-if="$slots.prefix">
      <slot name="prefix" />
    </template>
    <span
      v-if="$slots?.default && !isLoading"
      class="block max-w-full truncate text-center text-sm font-medium"
      :class="{ 'text-xs': size === ButtonSize.EXTRA_SMALL }"
    >
      <slot />
    </span>
    <WLIcon
      v-if="isLoading"
      :name="IconName.NAVIGATION_LOADING"
      :has-hover="false"
      :size="iconSize"
      class="shrink-0 animate-spin"
    />
    <WLIcon
      v-else-if="icon"
      :name="icon"
      :has-hover="false"
      :size="iconSize"
      class="shrink-0"
    />
  </WLLink>
</template>

<script setup lang="ts">
import {
  type ButtonEmits,
  type ButtonProps,
  ButtonSize,
  ButtonType,
  ButtonVariant,
} from "./types";
import { WLLink } from "../link";
import { computed, toRefs, useSlots, useTemplateRef } from "vue";
import { IconName, IconSize, WLIcon } from "../icon";
import { useWebHaptics } from "../../composables/use-web-haptics";

const emit = defineEmits<ButtonEmits>();
const props = withDefaults(defineProps<ButtonProps>(), {
  as: "button",
  variant: ButtonVariant.DEFAULT,
  type: ButtonType.PRIMARY,
  size: ButtonSize.MEDIUM,
  isFullWidth: false,
  isDisabled: false,
  isVertical: false,
  isLoading: false,
  isSubmit: false,
});

const {
  as,
  type,
  size,
  isFullWidth,
  isDisabled,
  icon,
  isVertical,
  isLoading,
  isSubmit,
  to,
  href,
} = toRefs(props);

const slots = useSlots();
const elRef = useTemplateRef<HTMLButtonElement | HTMLLinkElement>("elRef");
const { triggerHaptic } = useWebHaptics();
const buttonType = computed<"submit" | "button" | null>(() => {
  if (isSubmit.value) return "submit";
  if (as.value === "button") return "button";
  return null;
});

const linkDisabled = computed(() =>
  as.value === "button" || isLoading.value ? isDisabled.value : null,
);
const linkTo = computed(() => (as.value === "a" ? to.value : undefined));
const linkHref = computed(() => (as.value === "a" ? href.value : undefined));

const onClick = (e: MouseEvent): void => {
  if (isDisabled.value) {
    if (as.value === "a") {
      e.preventDefault();
    }
    return;
  }
  triggerHaptic("rigid");
  emit("click", e);
};

const iconSize = computed<IconSize>(() => {
  switch (size.value) {
    case ButtonSize.EXTRA_SMALL:
    case ButtonSize.SMALL:
      return IconSize.SMALL;
    default:
      return IconSize.MEDIUM;
  }
});

defineExpose({ elRef });
</script>

<style>
@reference "@theme";

/* Follow the format, type-$type class and inside all the variants with variant-$variant. */
.wl-button {
  &.type-primary {
    @apply bg-accent text-inverted;

    &.variant-default {
      &:hover {
        @apply bg-accent-hover;
      }

      &:active {
        @apply bg-accent-emphasis;
      }

      &:focus-visible {
        @apply outline-accent;
      }
    }
  }

  &.type-secondary {
    @apply bg-muted text-emphasis;

    &.variant-default {
      &:not(.is-disabled) {
        &:hover {
          @apply bg-emphasis;
        }

        &:active {
          @apply bg-pressed;
        }

        &:focus-visible {
          @apply outline-accent;
        }
      }
    }

    /* VARIANTS */
    &.variant-elevated {
      @apply border-default bg-default text-emphasis md:shadow-down-sm border-solid md:border-2;

      &:not(.is-disabled) {
        &:hover {
          @apply bg-muted;
        }

        &:active {
          @apply bg-emphasis;
        }

        &:focus-visible {
          @apply outline-accent;
        }
      }
    }

    &.variant-dashed {
      @apply border-muted bg-theme-transparent text-emphasis border border-dashed;

      &:not(.is-disabled) {
        &:hover {
          @apply bg-muted;
        }

        &:active {
          @apply bg-emphasis;
        }

        &:focus-visible {
          @apply outline-accent;
        }
      }
    }

    &.variant-plain {
      @apply bg-default text-emphasis border border-solid border-white;

      &:not(.is-disabled) {
        &:hover {
          @apply bg-muted;
        }

        &:active {
          @apply bg-emphasis;
        }

        &:focus-visible {
          @apply outline-accent;
        }
      }
    }
  }

  &.type-ai {
    &.variant-default {
      @apply from-ai-from via-ai-via to-ai-to bg-gradient-to-r text-white;
      background-size: 200% 100%;
      animation: gradient-shift 3s ease-in-out infinite;
    }

    &.variant-outline {
      @apply border-muted text-emphasis border border-solid bg-transparent;

      &:not(.is-disabled) {
        &:hover {
          @apply border-ai bg-ai/10;
        }

        &:active {
          @apply border-ai bg-ai/20;
        }

        &:focus-visible {
          @apply outline-accent;
        }
      }

      &:not(.is-loading) {
        @apply justify-start;
      }

      /* loading indicator */
      .wl-icon {
        @apply text-ai;
      }
    }
  }

  &.size-small.icon-only {
    @apply w-6;
  }

  &.size-xs {
    @apply h-6 px-2;
  }

  &.size-sm {
    @apply h-8 min-w-8 px-2;
  }

  &.size-md {
    @apply h-10 min-w-10 px-3;
  }

  &.is-vertical {
    @apply h-14 flex-col-reverse items-center justify-center gap-0.5;

    span {
      @apply text-xs;
    }

    .icon-wrapper {
      @apply h-6;
    }
  }

  &:focus-visible {
    @apply outline outline-1 outline-offset-2;
  }
}
</style>
